import { Box, Text } from '@chakra-ui/react';
import type { SimpleMarkdownProps } from '@udacity/ureact-markdown/simple-markdown';

import { Link } from '~/components/link';

/** Component overrides for {@link SimpleMarkdown}; merge with {@link mergeMarkdownComponents} when a surface needs tweaks. */
export type MarkdownChakraComponents = NonNullable<SimpleMarkdownProps['components']>;

export function mergeMarkdownComponents(
  base: MarkdownChakraComponents,
  overrides: Partial<MarkdownChakraComponents>
): MarkdownChakraComponents {
  return { ...base, ...overrides };
}

/**
 * Body copy on dark grey backgrounds (e.g. LP v2 hero long description): grey text, purple links, lists.
 * Pair with {@link Markdown} from ~/components/markdown for sanitization and GFM.
 */
export const darkGreyHeroMarkdownComponents: MarkdownChakraComponents = {
  a: ({ href, rel, children }) => (
    <Link
      _hover={{ color: 'blue.100' }}
      color='purple.300'
      fontSize='16px'
      href={href ?? '#'}
      isExternal={Boolean(href?.startsWith('http'))}
      lineHeight='tall'
      rel={rel}
      textDecoration='underline'
    >
      {children}
    </Link>
  ),
  li: ({ children }) => (
    <Box _last={{ mb: 0 }} as='li' mb={1}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box
      as='ol'
      color='grey.200'
      fontSize='16px'
      lineHeight='tall'
      mb={3}
      pl='1.25em'
      sx={{ listStyleType: 'decimal' }}
    >
      {children}
    </Box>
  ),
  p: ({ children }) => (
    <Text _last={{ mb: 0 }} color='grey.200' fontSize='16px' lineHeight='tall' mb={3}>
      {children}
    </Text>
  ),
  strong: ({ children }) => (
    <Text as='strong' color='grey.200' fontWeight='700'>
      {children}
    </Text>
  ),
  ul: ({ children }) => (
    <Box as='ul' color='grey.200' fontSize='16px' lineHeight='tall' mb={3} pl='1.25em' sx={{ listStyleType: 'disc' }}>
      {children}
    </Box>
  ),
};
